/**
 * eBay sale-sync — client side. "Sync eBay Sales" pulls recent sold orders via
 * the ebay-orders Edge Function into a pending queue, shown in a review modal.
 * "Record sale" PREFILLS the existing Sale form from the order (platform,
 * buyer, date, per-item prices, eBay fees, shipping) so every dollar flows
 * through the proven saveSale machinery — COGS, inventory decrement, status
 * flips, tax fields. After that sale saves Completed, the order row is marked
 * imported (hook in saveSale via the _ebayOrderMark window flag).
 */
import { sb } from "./supabase";
import { DB } from "./db";
import { escapeHtml, toast } from "./dom";
import { openModal, closeModal } from "../components/modal";
import { fmt$ } from "../utils/format";
import { editSale } from "../forms/sale";

interface EbayOrderLine { sku: string; title: string; qty: number; price: number }
interface EbayOrderRow {
  order_id: string; environment: string; order_date: string | null; buyer: string | null;
  subtotal: number | null; shipping: number | null; fees: number | null; total: number | null;
  line_items: EbayOrderLine[]; status: string;
}

let _pending: EbayOrderRow[] = [];

/** Match an order SKU to an inventory lot — cards first, then sealed. */
function lotForSku(sku: string): { kind: "card" | "sealed"; row: any } | null {
  if (!sku) return null;
  const card = DB.table("cards").find(c => c["Card Lot ID"] === sku);
  if (card) return { kind: "card", row: card };
  const sealed = DB.table("sealed").find(sl => sl["Lot ID"] === sku);
  if (sealed) return { kind: "sealed", row: sealed };
  return null;
}

export async function syncEbayOrders(silent = false): Promise<void> {
  if (!silent) toast("Checking eBay for new sales…");
  try {
    const { data, error } = await sb.functions.invoke("ebay-orders", { body: { action: "sync" } });
    if (error) throw error;
    const d = data as any;
    if (d?.error) throw new Error(d.error);
    _pending = (d?.pending || []) as EbayOrderRow[];
    if (!_pending.length) {
      if (!silent) toast(`eBay: no unrecorded sales (checked ${d?.fetched ?? 0} recent orders)`);
      return;
    }
    openEbayOrdersModal();
  } catch (e: any) {
    if (!silent) toast("eBay sync failed: " + (e?.message || e), "err");
  }
}

function ordersBodyHtml(): string {
  if (!_pending.length) {
    return `<div class="text-sm text-[var(--muted)] italic py-4 text-center">No pending eBay sales — all caught up.</div>`;
  }
  return _pending.map((o, i) => {
    const items = (o.line_items || []).map(li => {
      const lot = lotForSku(li.sku);
      const match = lot
        ? `<span class="text-emerald-300 font-mono text-xs">✓ ${escapeHtml(li.sku)}</span>`
        : (li.sku
          ? `<span class="text-amber-300 text-xs">SKU ${escapeHtml(li.sku)} — no matching lot</span>`
          : `<span class="text-amber-300 text-xs">no SKU — will import free-text</span>`);
      return `<div class="text-xs">${escapeHtml(li.title)} ×${li.qty} · ${fmt$(li.price)} ${match}</div>`;
    }).join("");
    return `
      <div class="panel p-2" data-ebay-order="${i}">
        <div class="flex items-center gap-2 flex-wrap mb-1">
          <span class="pill pill-blue text-xs">eBay</span>
          <b class="text-sm">${escapeHtml((o.order_date || "").slice(0, 10))}</b>
          <span class="text-xs text-[var(--muted)]">order ${escapeHtml(o.order_id)}</span>
          ${o.buyer ? `<span class="text-xs">buyer: <b>${escapeHtml(o.buyer)}</b></span>` : ""}
          <span class="ml-auto stat-num font-bold">${fmt$(o.total ?? o.subtotal ?? 0)}</span>
        </div>
        <div class="mb-2 space-y-0.5">${items}</div>
        <div class="text-xs text-[var(--muted)] mb-2">Fees ${fmt$(o.fees ?? 0)} · Shipping ${fmt$(o.shipping ?? 0)}</div>
        <div class="flex gap-2">
          <button class="btn btn-primary !py-1 !px-3 text-xs" onclick="_ebaySalesRecord(${i})">Record sale</button>
          <button class="btn btn-ghost !py-1 !px-3 text-xs" onclick="_ebaySalesIgnore(${i})" title="Skip this order (won't be offered again)">Ignore</button>
        </div>
      </div>`;
  }).join("");
}

function openEbayOrdersModal(): void {
  const body = `
    <div class="text-xs text-[var(--muted)] mb-3">Sold on eBay, not yet in your books. <b>Record sale</b> opens the normal Sale form prefilled from the order — review and Save. Items are matched to lots by SKU (your lot IDs).</div>
    <div id="ebayOrdersBody" class="space-y-2 max-h-[60vh] overflow-y-auto pr-1">${ordersBodyHtml()}</div>`;
  openModal({
    title: `eBay sales to record (${_pending.length})`,
    body,
    footer: `<button class="btn btn-ghost" onclick="closeModal()">Close</button>`,
    wide: true,
  });
}

async function recordOrder(i: number): Promise<void> {
  const o = _pending[i];
  if (!o) return;
  closeModal();
  await editSale(undefined);
  const state = (window as any)._saleState;
  if (!state || !Array.isArray(state.Items)) { toast("Couldn't open the sale form", "err"); return; }
  for (const li of (o.line_items || [])) {
    const lot = lotForSku(li.sku);
    if (lot) {
      state.Items.push({
        "Lot ID": li.sku,
        "Item": String((lot.kind === "card" ? lot.row["Card Name"] : lot.row["Product"]) || li.title),
        "Type": lot.kind === "card" ? "Card" : "Sealed",
        "Qty": Number(li.qty) || 1,
        "Sale Price": Number(li.price) || 0,
        "COGS": Number(lot.row["Unit Basis"]) || 0,
      });
    } else {
      state.Items.push({
        "Item": li.title + (li.sku ? ` (SKU ${li.sku})` : ""),
        "Type": "Card",
        "Qty": Number(li.qty) || 1,
        "Sale Price": Number(li.price) || 0,
        "COGS": 0,
      });
    }
  }
  const form = document.getElementById("saleForm");
  const set = (name: string, val: string) => {
    const el = form?.querySelector(`[name="${name}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    if (el) el.value = val;
  };
  if (o.order_date) set("Date", o.order_date.slice(0, 10));
  set("Platform", "eBay");
  set("Buyer", o.buyer || "");
  set("Fees", String(o.fees ?? 0));
  set("Shipping", String(o.shipping ?? 0));
  set("Notes", `eBay order ${o.order_id}`);
  (window as any).renderSaleItemsList?.();
  (window as any).markModalDirty?.();
  // saveSale consumes this flag after a successful Completed save.
  (window as any)._ebayOrderMark = { orderId: o.order_id, saleId: state["Sale ID"] };
  toast("Review the prefilled sale, then Save to record it");
}

async function ignoreOrder(i: number): Promise<void> {
  const o = _pending[i];
  if (!o) return;
  await markEbayOrder(o.order_id, "ignored");
  _pending.splice(i, 1);
  const el = document.getElementById("ebayOrdersBody");
  if (el) el.innerHTML = ordersBodyHtml();
  toast("Order ignored");
}

export async function markEbayOrder(orderId: string, status: "pending" | "imported" | "ignored", saleId?: string): Promise<void> {
  try {
    await sb.functions.invoke("ebay-orders", { body: { action: "mark", orderId, status, saleId } });
  } catch (e) {
    console.warn("[ebaySales] mark failed:", e);
  }
}

// Inline-handler + cross-module bridges (avoids a sale.ts <-> ebaySales import cycle)
(window as any).syncEbayOrders   = () => { void syncEbayOrders(); };
(window as any)._ebaySalesRecord = (i: number) => { void recordOrder(i); };
(window as any)._ebaySalesIgnore = (i: number) => { void ignoreOrder(i); };
(window as any)._ebayOrderMarkFn = markEbayOrder;
